/**
 * How an effective date range is named on the Manpower screens.
 *
 * A Capacity_Period is identified by its effective range rather than by a calendar
 * identifier, and every screen that shows one has to write it out: the Capacity &
 * Utilisation strip and reconciliation table, and the Capacity Inputs register.
 * They must agree, so the rule lives here once.
 *
 * ## Parts, not text
 *
 * {@link periodLabelOfRange} returns a descriptor and {@link periodText} renders it.
 * That split does two things: it keeps R18.9's "no user-facing text outside the
 * catalogue" true for the derivation (month names come from `month.*` through `t`),
 * and it lets the same period be named at two lengths — compact for a narrow grid
 * column, full for a table cell or a tooltip — without a second parse or two
 * competing ideas of how a period is written.
 *
 * Raw `yyyy-mm-dd` never reaches a reader: `2026-09-17 – 2026-12-31` is a wall of
 * digits that reads as data rather than as a date.
 */
import type { TFunction } from 'i18next';

/** One calendar date, split for display. */
export interface DayParts {
  readonly day: number;
  readonly month: number;
  readonly year: number;
}

/** How a period should be named on screen. */
export type PeriodLabel =
  | { readonly kind: 'month'; readonly month: number; readonly year: number }
  | { readonly kind: 'year'; readonly year: number }
  | {
      readonly kind: 'monthRange';
      readonly fromMonth: number;
      readonly toMonth: number;
      readonly year: number;
    }
  /** A range covering a single day, which reads as that one date rather than as `X – X`. */
  | { readonly kind: 'day'; readonly day: number; readonly month: number; readonly year: number }
  /** An arbitrary range, in parts so the renderer can state a shared year once. */
  | {
      readonly kind: 'dayRange';
      readonly from: DayParts;
      readonly to: DayParts;
      /** Whether both ends fall in the same calendar year. */
      readonly sameYear: boolean;
    }
  /** The fallback for a range whose dates are not `yyyy-mm-dd`; kept verbatim. */
  | { readonly kind: 'dateRange'; readonly start: string; readonly end: string };

/** A `yyyy-mm-dd` date split into parts, or `undefined` when it is not one. */
function parseIsoDate(value: string): DayParts | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) {
    return undefined;
  }
  const [, year, month, day] = match;
  return {
    year: Number.parseInt(year ?? '', 10),
    month: Number.parseInt(month ?? '', 10),
    day: Number.parseInt(day ?? '', 10),
  };
}

/** The last day of a month, so a range can be recognised as covering it exactly. */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Names an effective date range (R6.5, UI spec §5.1).
 *
 * Recognised shapes, in the order a reader would say them out loud: exactly one
 * calendar month reads as that month; exactly one calendar year as the year; a whole
 * number of months within one year as a month span; a single day as that date; and
 * anything else as its two endpoints in parts.
 */
export function periodLabelOfRange(
  effectivePeriodStart: string,
  effectivePeriodEnd: string,
): PeriodLabel {
  const start = parseIsoDate(effectivePeriodStart);
  const end = parseIsoDate(effectivePeriodEnd);

  if (start === undefined || end === undefined) {
    return { kind: 'dateRange', start: effectivePeriodStart, end: effectivePeriodEnd };
  }

  // A one-day period, which several Capacity_Periods in this product legitimately
  // are. `2026-12-31 – 2026-12-31` says the same thing twice and reads as an error.
  if (start.year === end.year && start.month === end.month && start.day === end.day) {
    return { kind: 'day', day: start.day, month: start.month, year: start.year };
  }

  const startsMonth = start.day === 1;
  const endsMonth = end.day === lastDayOfMonth(end.year, end.month);

  if (startsMonth && endsMonth && start.year === end.year) {
    if (start.month === end.month) {
      return { kind: 'month', month: start.month, year: start.year };
    }
    if (start.month === 1 && end.month === 12) {
      return { kind: 'year', year: start.year };
    }
    return { kind: 'monthRange', fromMonth: start.month, toMonth: end.month, year: start.year };
  }

  return { kind: 'dayRange', from: start, to: end, sameYear: start.year === end.year };
}

/**
 * A {@link PeriodLabel} as display text (R18.9).
 *
 * `compact` drops the year, for a grid column a twelfth of a card wide where the
 * planning year is already a filter on the same screen. `full` always carries it,
 * for a table cell or a tooltip a reader might see out of context.
 *
 * All-caps month abbreviations for `compact`, title case for `full`: `MAR` sits in an
 * uppercase 10.5px micro-label, `Mar 2026` in a normal-case cell, and reusing the
 * caps form there reads as shouting.
 */
export function periodText(
  t: TFunction,
  label: PeriodLabel,
  variant: 'compact' | 'full',
): string {
  const compact = variant === 'compact';
  const month = (value: number): string =>
    t(`month.${compact ? 'short' : 'abbr'}.${String(value)}`);
  /** `17 Sep`, and with the year when the caller wants a self-contained date. */
  const day = (parts: DayParts, withYear: boolean): string =>
    `${String(parts.day)} ${month(parts.month)}${withYear ? ` ${String(parts.year)}` : ''}`;

  switch (label.kind) {
    case 'month':
      return compact ? month(label.month) : `${month(label.month)} ${String(label.year)}`;
    case 'year':
      return String(label.year);
    case 'monthRange':
      return compact
        ? `${month(label.fromMonth)}–${month(label.toMonth)}`
        : `${month(label.fromMonth)}–${month(label.toMonth)} ${String(label.year)}`;
    case 'day':
      return day(label, !compact);
    case 'dayRange':
      // The year is stated once for a range that stays inside one year, and on both
      // ends when it crosses — the shortest form that is still unambiguous.
      return label.sameYear
        ? `${day(label.from, false)} – ${day(label.to, !compact)}`
        : `${day(label.from, true)} – ${day(label.to, true)}`;
    case 'dateRange':
      return t('manpower.capacityUtilisation.periodRange', {
        start: label.start,
        end: label.end,
      });
  }
}

/** Chronological order over {@link PeriodLabel}, earliest first. */
export function comparePeriodLabels(a: PeriodLabel, b: PeriodLabel): number {
  return sortKeyOf(a).localeCompare(sortKeyOf(b));
}

/** A lexicographically sortable stand-in for a label's start point. */
function sortKeyOf(label: PeriodLabel): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  switch (label.kind) {
    case 'month':
      return `${String(label.year)}-${pad(label.month)}-01`;
    case 'year':
      return `${String(label.year)}-01-01`;
    case 'monthRange':
      return `${String(label.year)}-${pad(label.fromMonth)}-01`;
    case 'day':
      return `${String(label.year)}-${pad(label.month)}-${pad(label.day)}`;
    case 'dayRange':
      return `${String(label.from.year)}-${pad(label.from.month)}-${pad(label.from.day)}`;
    case 'dateRange':
      return label.start;
  }
}
